import { useId, useMemo, useState, type ReactNode } from "react";
import {
  calibrationMarker,
  evaluateCalibration,
  formatCalibrationFactor,
  type CalibrationEvaluation,
  type CalibrationRequest,
  type RoomCalibration,
  type RoomPlanCapture,
  type ScanElement,
  type ScanRoomGroup,
} from "@sts/geometry";
import { formatArea } from "@sts/scan-viewer";
import type {
  HeatLossAfterCalibration,
  ScanCalibration,
  ScanCalibrationList,
} from "../api/types.js";
import { usePortal } from "../app/context.js";
import { useSubmit } from "../app/use-submit.js";
import { HowToLink } from "../help/HowToLink.jsx";
import { heatLossWordsOf, ReviewNoteList, type HeatLossWords } from "./heat-loss-words.jsx";
import { Failure } from "../components/Async.jsx";
import {
  formatDeviation,
  formatMm,
  parseLaserInput,
  type LaserParse,
  type LaserUnit,
} from "./calibration.js";

/**
 * Calibrating the scan from a laser distance meter, on the Survey page.
 *
 * OPTIONAL (Josh, 2026-09-11: "keep this option only if anyone needs that
 * level of accuracy, otherwise person can move on"). An uncalibrated scan is
 * not a problem: its figures read "LiDAR scan" in plain muted text, and the
 * only trace of this feature is one quiet "Calibrate with a laser reading"
 * button for those who want it — no prompt, banner, warning or colour.
 *
 * The flow, when someone does want it: pick one wall (the wall tapped on the
 * plan, in 3D or in the table is already picked), type what the laser read,
 * Apply — and the WHOLE scan is corrected. "More options" holds the extras,
 * closed by default: this room only, a check reading on a second wall, the
 * laser's make. The factor, and every refusal, come from `@sts/geometry`'s
 * `evaluateCalibration` run over the RAW capture — the same function the API
 * runs again before it stores anything, so the preview and the server cannot
 * disagree.
 *
 * A calibrated room says so — "calibrated ×1.0071 from a laser reading
 * (J. Ryder, 11 Sep)" — with the readings behind it and a way to remove it.
 *
 * THE HEAT LOSS FOLLOWS. Applying or removing a calibration re-converts the
 * survey's heat-loss rooms from the scan on the server, and `onChanged` makes
 * the page re-read them. The rooms are merged: the scan's own figures take
 * the calibration, and what a person entered or confirmed (a U-value, a party
 * wall, a typed area) is kept. When something of theirs was kept, the API
 * says so in one plain sentence — with anything to check, such as a typed
 * area the calibrated scan now disagrees with — shown here right after the
 * Apply or Remove that caused it. When the rooms cannot follow at all (the
 * survey is complete) the calibration still stands and the sentence says so.
 * Otherwise nothing is said: the figures simply change (`heatLossWordsOf`).
 */

export interface CalibrationPanelProps {
  readonly surveyId: string;
  /** The capture as the API served it — never a calibrated one. */
  readonly rawCapture: RoomPlanCapture;
  /** `scanRoomGroups(rawCapture)`: the scanned figures a factor is taken from. */
  readonly rawGroups: readonly ScanRoomGroup[];
  readonly roomIndex: number;
  /** This room's entry from `applyCalibration(...).rooms`. */
  readonly status: RoomCalibration | null;
  /**
   * The survey's calibrations. `null` when the server predates them (0040).
   * `loading` covers the first load only: a reload keeps the last list on
   * screen, so applying one does not flash the figures back to the scan's.
   */
  readonly calibrations: ScanCalibrationList | null;
  readonly calibrationsState: "loading" | "ready" | "failed";
  /** A wall picked on the plan, in 3D or in the table — the form's first choice of reference. */
  readonly selectedElementId?: string | undefined;
  readonly canCalibrate: boolean;
  /** Something was applied or removed: reload the calibrations, and the heat-loss figures with them. */
  readonly onChanged: () => void;
}

export function CalibrationPanel({
  surveyId,
  rawCapture,
  rawGroups,
  roomIndex,
  status,
  calibrations,
  calibrationsState,
  selectedElementId,
  canCalibrate,
  onChanged,
}: CalibrationPanelProps): ReactNode {
  const [open, setOpen] = useState(false);
  // Set only by the answer to an Apply or Remove made here, so it can never
  // appear on a survey someone merely opened.
  const [heatLossNote, setHeatLossNote] = useState<HeatLossWords | null>(null);
  const calibrated = status !== null && status.source !== "none";
  const held = calibrated
    ? (calibrations?.calibrations.find((c) => c.id === status.calibration?.id) ?? null)
    : null;
  const group = rawGroups[roomIndex];

  return (
    <div className="scan-calibration" aria-label="Laser calibration">
      <p className="scan-calibration__marker">
        {/* Until the list loads the figures on screen ARE the scan's own, so
            they say so; nothing is shown as pending or missing. */}
        <span
          className={calibrated ? "pill pill--ok" : "muted small"}
          data-testid="calibration-marker"
        >
          {calibrationMarker(calibrationsState === "loading" ? null : status)}
        </span>
      </p>

      {held !== null && status !== null ? (
        <InForce
          surveyId={surveyId}
          calibration={held}
          source={status.source}
          canRemove={canCalibrate}
          onRemoved={(outcome) => {
            setHeatLossNote(heatLossWordsOf(outcome));
            onChanged();
          }}
        />
      ) : null}

      {heatLossNote !== null ? (
        <div className="scan-calibration__heat-loss" role="status">
          <p className="muted small" data-testid="calibration-heat-loss-note">
            {heatLossNote.note}
          </p>
          <ReviewNoteList
            notes={heatLossNote.reviewNotes}
            className="muted small scan-calibration__review-notes"
            testId="calibration-review-notes"
          />
        </div>
      ) : null}

      {calibrationsState === "loading" ? null : calibrationsState === "failed" ? (
        // A load failure, not a verdict on the scan: said once, quietly, so a
        // calibration that exists is not silently missing from the figures.
        <p className="muted small" role="status">
          Laser calibrations could not be loaded; the figures shown are the scan's own.
        </p>
      ) : calibrations === null ? null : canCalibrate && group !== undefined ? (
        open ? (
          <CalibrationForm
            surveyId={surveyId}
            rawCapture={rawCapture}
            rawGroups={rawGroups}
            group={group}
            status={status}
            selectedElementId={selectedElementId}
            onCancel={() => setOpen(false)}
            onApplied={(outcome) => {
              setOpen(false);
              setHeatLossNote(heatLossWordsOf(outcome));
              onChanged();
            }}
          />
        ) : (
          <p className="scan-calibration__start">
            <button
              type="button"
              className="secondary small"
              onClick={() => {
                setHeatLossNote(null);
                setOpen(true);
              }}
            >
              Calibrate with a laser reading
            </button>{" "}
            <HowToLink guide="laser-calibration">When and how</HowToLink>
          </p>
        )
      ) : null}
    </div>
  );
}

function InForce({
  surveyId,
  calibration,
  source,
  canRemove,
  onRemoved,
}: {
  readonly surveyId: string;
  readonly calibration: ScanCalibration;
  readonly source: RoomCalibration["source"];
  readonly canRemove: boolean;
  readonly onRemoved: (heatLoss: HeatLossAfterCalibration | undefined) => void;
}): ReactNode {
  const { client } = usePortal();
  const { busy, error, submit } = useSubmit();
  const { reference, check } = calibration;
  return (
    <div className="scan-calibration__held">
      <p className="small">
        {source === "scan" ? "The whole scan is calibrated from " : "This room is calibrated from "}
        <strong>{reference.label}</strong>: laser {formatMm(reference.laserMm)}, scanned{" "}
        {reference.scannedM.toFixed(3)} m ({formatCalibrationFactor(calibration.factor)},{" "}
        {formatDeviation(calibration.factor)}).{" "}
        {check ? (
          <>
            Checked on <strong>{check.label}</strong>: laser {formatMm(check.laserMm)}, corrected scan{" "}
            {check.correctedM.toFixed(3)} m — {Math.abs(check.residualMm).toFixed(0)} mm apart, within{" "}
            {check.toleranceMm.toFixed(0)} mm.
          </>
        ) : null}
        {calibration.laserDevice ? <> Laser: {calibration.laserDevice}.</> : null}
        {calibration.source === "phone" ? <> Entered on the phone.</> : null}
      </p>
      {canRemove ? (
        <button
          type="button"
          className="secondary"
          disabled={busy}
          onClick={() =>
            void submit(async () => {
              const withdrawn = await client.withdrawScanCalibration(surveyId, calibration.id);
              onRemoved(withdrawn?.heatLoss);
            })
          }
        >
          {busy
            ? "Removing…"
            : source === "scan"
              ? "Remove the whole-scan calibration"
              : "Remove this calibration"}
        </button>
      ) : null}
      {error !== null ? <Failure error={error} what="removing the calibration" /> : null}
    </div>
  );
}

interface ReadingDraft {
  readonly wallId: string;
  readonly text: string;
  readonly unit: LaserUnit;
}

function wallsOf(groups: readonly ScanRoomGroup[]): readonly ScanElement[] {
  return groups.flatMap((g) => g.elements.filter((e) => e.kind === "wall"));
}

function longest(walls: readonly ScanElement[]): ScanElement | undefined {
  return walls.reduce<ScanElement | undefined>((best, w) => (best && best.widthM >= w.widthM ? best : w), undefined);
}

function CalibrationForm({
  surveyId,
  rawCapture,
  rawGroups,
  group,
  status,
  selectedElementId,
  onCancel,
  onApplied,
}: {
  readonly surveyId: string;
  readonly rawCapture: RoomPlanCapture;
  readonly rawGroups: readonly ScanRoomGroup[];
  readonly group: ScanRoomGroup;
  readonly status: RoomCalibration | null;
  readonly selectedElementId: string | undefined;
  readonly onCancel: () => void;
  readonly onApplied: (heatLoss: HeatLossAfterCalibration | undefined) => void;
}): ReactNode {
  const { client } = usePortal();
  const { busy, error, submit } = useSubmit();
  const ids = useId();
  const roomRef = group.externalRef ?? status?.roomRef ?? "";
  const multiRoom = rawGroups.length > 1;

  // The extras, all closed/off by default: the ordinary flow is one wall, one
  // reading, the whole scan.
  const [moreOpen, setMoreOpen] = useState(false);
  const [thisRoomOnly, setThisRoomOnly] = useState(false);
  const [checkOn, setCheckOn] = useState(false);
  const [laserDevice, setLaserDevice] = useState("");

  const roomWalls = useMemo(() => wallsOf([group]), [group]);
  const allWalls = useMemo(() => wallsOf(rawGroups), [rawGroups]);
  const scopeWalls = thisRoomOnly ? roomWalls : allWalls;
  // The wall picked on the plan / in 3D / in the table, else this room's
  // longest (the best reference), else the scan's longest.
  const firstChoice =
    scopeWalls.find((w) => w.id === selectedElementId) ?? longest(roomWalls) ?? longest(scopeWalls);
  const [reference, setReference] = useState<ReadingDraft>({
    wallId: firstChoice?.id ?? "",
    text: "",
    unit: "mm",
  });
  const [check, setCheck] = useState<ReadingDraft>({ wallId: "", text: "", unit: "mm" });

  // A wall no longer offered (the scope narrowed) falls back to the first choice.
  const referenceId = scopeWalls.some((w) => w.id === reference.wallId)
    ? reference.wallId
    : (firstChoice?.id ?? "");
  const checkWalls = scopeWalls.filter((w) => w.id !== referenceId);
  const checkId = checkWalls.some((w) => w.id === check.wallId) ? check.wallId : (longest(checkWalls)?.id ?? "");

  const refParse = parseLaserInput(reference.text, reference.unit);
  const checkParse: LaserParse = checkOn ? parseLaserInput(check.text, check.unit) : { ok: "empty" };

  const request: CalibrationRequest | null =
    refParse.ok === true && referenceId !== "" && (!checkOn || checkParse.ok === true)
      ? {
          scope: thisRoomOnly ? { kind: "room", roomRef } : { kind: "scan" },
          reference: { span: { kind: "wall", elementId: referenceId }, laserMm: refParse.mm },
          check:
            checkOn && checkParse.ok === true && checkId !== ""
              ? { span: { kind: "wall", elementId: checkId }, laserMm: checkParse.mm }
              : null,
        }
      : null;

  const evaluation = useMemo<CalibrationEvaluation | null>(
    () => (request ? evaluateCalibration(rawCapture, request) : null),
    // The request is rebuilt every render; its content is what matters.
    [rawCapture, JSON.stringify(request)],
  );

  const roomKeepsOwn = !thisRoomOnly && status?.source === "room";
  const label = (w: ScanElement) =>
    `${w.label}${!thisRoomOnly && multiRoom ? `, ${w.roomLabel}` : ""} — ${w.widthM.toFixed(3)} m scanned`;

  function apply(): void {
    if (!request || !evaluation?.ok) return;
    void submit(async () => {
      const recorded = await client.recordScanCalibration(surveyId, {
        ...request,
        ...(laserDevice.trim() !== "" ? { laserDevice: laserDevice.trim() } : {}),
      });
      onApplied(recorded.heatLoss);
    });
  }

  return (
    <form
      className="scan-calibration__form"
      aria-label="Calibrate with a laser reading"
      onSubmit={(event) => {
        event.preventDefault();
        apply();
      }}
    >
      <ReadingFields
        idPrefix={`${ids}-ref`}
        legend="Laser reading"
        wallLabel="Wall"
        walls={scopeWalls}
        wallId={referenceId}
        draft={reference}
        parse={refParse}
        optionLabel={label}
        hint={
          thisRoomOnly
            ? `Corrects ${group.label} only. A long wall makes the best reference.`
            : "Corrects every room in the scan. A long wall makes the best reference."
        }
        onChange={setReference}
      />

      <div className="scan-calibration__more">
        <button
          type="button"
          className="link small"
          aria-expanded={moreOpen}
          aria-controls={`${ids}-more`}
          onClick={() => setMoreOpen((v) => !v)}
        >
          {moreOpen ? "Fewer options" : "More options"}
        </button>
        {moreOpen ? (
          <div id={`${ids}-more`} className="scan-calibration__extras">
            <label className="field checkbox">
              <input
                type="checkbox"
                checked={thisRoomOnly}
                onChange={(e) => setThisRoomOnly(e.target.checked)}
              />
              Only correct this room ({group.label})
            </label>
            {multiRoom ? (
              <p className="muted small">
                For a multi-room scan whose rooms drifted differently. A room's own calibration takes
                precedence over the whole-scan one for that room.
              </p>
            ) : null}

            <label className="field checkbox">
              <input type="checkbox" checked={checkOn} onChange={(e) => setCheckOn(e.target.checked)} />
              Add a check reading on another wall
            </label>
            {checkOn ? (
              <ReadingFields
                idPrefix={`${ids}-check`}
                legend="Check reading"
                wallLabel="Check wall"
                walls={checkWalls}
                wallId={checkId}
                draft={{ ...check, wallId: checkId }}
                parse={checkParse}
                optionLabel={label}
                hint="The corrected scan must agree with this within 10 mm, or 0.5% on a wall over 2 m."
                onChange={(next) => setCheck(next)}
              />
            ) : null}

            <div className="field">
              <label htmlFor={`${ids}-device`}>Laser meter</label>
              <input
                id={`${ids}-device`}
                value={laserDevice}
                maxLength={120}
                placeholder="e.g. Leica DISTO D2"
                onChange={(e) => setLaserDevice(e.target.value)}
              />
            </div>
          </div>
        ) : null}
      </div>

      <div className="scan-calibration__preview" aria-live="polite">
        <Preview
          evaluation={evaluation}
          checkOn={checkOn}
          group={group}
          roomKeepsOwn={roomKeepsOwn}
        />
      </div>

      <div className="scan-calibration__actions">
        <button type="submit" disabled={busy || !evaluation?.ok}>
          {busy ? "Applying…" : "Apply calibration"}
        </button>
        <button type="button" className="secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
      {error !== null ? <Failure error={error} what="the calibration" /> : null}
    </form>
  );
}

function ReadingFields({
  idPrefix,
  legend,
  wallLabel,
  walls,
  wallId,
  draft,
  parse,
  optionLabel,
  hint,
  onChange,
}: {
  readonly idPrefix: string;
  readonly legend: string;
  readonly wallLabel: string;
  readonly walls: readonly ScanElement[];
  readonly wallId: string;
  readonly draft: ReadingDraft;
  readonly parse: LaserParse;
  readonly optionLabel: (wall: ScanElement) => string;
  readonly hint: string;
  readonly onChange: (next: ReadingDraft) => void;
}): ReactNode {
  return (
    <fieldset className="scan-calibration__reading">
      <legend>{legend}</legend>
      <div className="field">
        <label htmlFor={`${idPrefix}-wall`}>{wallLabel}</label>
        <select
          id={`${idPrefix}-wall`}
          value={wallId}
          onChange={(e) => onChange({ ...draft, wallId: e.target.value })}
        >
          {walls.map((w) => (
            <option key={w.id} value={w.id}>
              {optionLabel(w)}
            </option>
          ))}
        </select>
      </div>
      <div className="scan-calibration__laser">
        <div className="field">
          <label htmlFor={`${idPrefix}-laser`}>{`${legend} (${draft.unit})`}</label>
          <input
            id={`${idPrefix}-laser`}
            inputMode="decimal"
            autoComplete="off"
            value={draft.text}
            placeholder={draft.unit === "mm" ? "e.g. 4230" : "e.g. 4.230"}
            onChange={(e) => onChange({ ...draft, wallId, text: e.target.value })}
          />
        </div>
        <div className="field">
          <label htmlFor={`${idPrefix}-unit`}>Unit</label>
          <select
            id={`${idPrefix}-unit`}
            value={draft.unit}
            onChange={(e) => onChange({ ...draft, wallId, unit: e.target.value as LaserUnit })}
          >
            <option value="mm">mm</option>
            <option value="m">m</option>
          </select>
        </div>
      </div>
      {parse.ok === false ? (
        <p className="scan-calibration__error small" role="alert">
          {parse.message}
        </p>
      ) : parse.ok === true && draft.unit === "m" ? (
        <p className="muted small">= {formatMm(parse.mm)}</p>
      ) : null}
      <p className="muted small">{hint}</p>
    </fieldset>
  );
}

function Preview({
  evaluation,
  checkOn,
  group,
  roomKeepsOwn,
}: {
  readonly evaluation: CalibrationEvaluation | null;
  readonly checkOn: boolean;
  readonly group: ScanRoomGroup;
  readonly roomKeepsOwn: boolean;
}): ReactNode {
  if (evaluation === null) {
    return <p className="muted small">Type the laser's reading to see the correction before applying it.</p>;
  }
  if (!evaluation.ok) {
    return (
      <div className="notice fail" role="alert">
        <p>{evaluation.message}</p>
      </div>
    );
  }
  const { factor, reference, check } = evaluation;
  const walls = group.elements.filter((e) => e.kind === "wall");
  return (
    <>
      <p>
        <strong>{formatCalibrationFactor(factor)}</strong>{" "}
        <span className="muted">({formatDeviation(factor)})</span> — {reference.label} scanned{" "}
        {reference.scannedM.toFixed(3)} m, laser {formatMm(reference.laserMm)}.
      </p>
      {check ? (
        <p className="small">
          <span className="pill pill--ok">Check holds</span> {check.label}: corrected scan{" "}
          {check.correctedM.toFixed(3)} m, laser {(check.laserMm / 1000).toFixed(3)} m —{" "}
          {Math.abs(check.residualMm).toFixed(0)} mm apart, within the {check.toleranceMm.toFixed(0)} mm
          tolerance.
        </p>
      ) : checkOn ? (
        // Only for someone who chose to add a check; without one there is
        // nothing to say — the check is an extra, not a gap.
        <p className="small muted">Type the check reading to compare it with the corrected scan.</p>
      ) : null}
      {roomKeepsOwn ? (
        <p className="small muted">
          {group.label} keeps its own calibration; this one applies to the other rooms.
        </p>
      ) : (
        <div className="table-scroll">
          <table className="scan-calibration__table">
            <caption>Every wall of {group.label}, before and after</caption>
            <thead>
              <tr>
                <th scope="col">Wall</th>
                <th scope="col" className="n">Scanned</th>
                <th scope="col" className="n">Calibrated</th>
                <th scope="col" className="n">Area scanned</th>
                <th scope="col" className="n">Area calibrated</th>
              </tr>
            </thead>
            <tbody>
              {walls.map((w) => (
                <tr key={w.id}>
                  <th scope="row">{w.label}</th>
                  <td className="n">{w.widthM.toFixed(3)} m</td>
                  <td className="n">{(w.widthM * factor).toFixed(3)} m</td>
                  <td className="n">{formatArea(w.areaM2, "m")}</td>
                  <td className="n">{formatArea(w.areaM2 * factor * factor, "m")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="muted small">
        Lengths × {factor.toFixed(4)}, areas × {(factor * factor).toFixed(4)}, volumes ×{" "}
        {(factor ** 3).toFixed(4)}. The scan itself is kept as captured.
      </p>
    </>
  );
}
